import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Steps from "../../components/steps/Steps";
import clint_image_icon from "../../assets/images/clint_image_icon.svg";
import { createCompany, resetStatus } from "../../store/slices/userSlice";
import { fetchAllFormulas } from "../../store/slices/formulaSlice";
import StatusPopup from "../../components/popups/StatusPopup";
import EditSuccessPopup from "../../components/popups/EditSuccessPopup";
import CompanyActivationPopup from "../../components/popups/CompanyActivationPopup";
import CountryCreatePopup from "../../components/popups/CountryCreatePopup";
import StateCreatePopup from "../../components/popups/StateCreatePopup";
import CityCreatePopup from "../../components/popups/CityCreatePopup";
import {
  fetchCountries,
  fetchStates,
  fetchCities,
} from "../../store/slices/locationSlice";

const AddNewCompany = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const fromPath = location.state?.from || "/dashboard";
  const clientId = location.state?.id || "";

  const { isSuccess, errorMessage, loading } = useSelector(
    (state) => state.user,
  );
  const { formulaList } = useSelector((state) => state.formula);

  const [profileImage, setProfileImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [companyName, setCompanyName] = useState("");

  // LOCATION
  const {
    countries = [],
    states = [],
    cities = [],
  } = useSelector((state) => state.location || {});

  const [showCountryPopup, setShowCountryPopup] = useState(false);
  const [showStatePopup, setShowStatePopup] = useState(false);
  const [showCityPopup, setShowCityPopup] = useState(false);
  const [locationFormValues, setLocationFormValues] = useState({});
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [selectedStateId, setSelectedStateId] = useState("");
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const handleOpenLocationPopup = (type, data = {}) => {
    if (type === "country") {
      setShowCountryPopup(true);
    }
    if (type === "state") {
      setSelectedCountryId(data.countryId || "");
      setShowStatePopup(true);
    }
    if (type === "city") {
      setSelectedStateId(data.stateId || "");
      setShowCityPopup(true);
    }
  };

  const handleCountryCreated = (country) => {
    dispatch(fetchCountries());
    setTimeout(() => {
      setLocationFormValues((prev) => ({
        ...prev,
        Country: String(country.id),
        CountryId: String(country.id),
        State: "",
        StateId: "",
        City: "",
        CityId: "",
      }));
    }, 300);
  };

  const handleStateCreated = (state) => {
    setLocationFormValues((prev) => ({
      ...prev,
      State: String(state.id),
      StateId: String(state.id),
      City: "",
      CityId: "",
    }));
  };

  const handleCityCreated = (city) => {
    dispatch(fetchCities(city.stateId));
    setTimeout(() => {
      setLocationFormValues((prev) => ({
        ...prev,
        City: String(city.id),
        CityId: String(city.id),
      }));
    }, 300);
  };

  useEffect(() => {
    if (isSuccess && isFormSubmitting) {
      setShowSuccessPopup(true);
      setIsFormSubmitting(false);
    }
  }, [isSuccess, isFormSubmitting]);

  useEffect(() => {
    dispatch(fetchAllFormulas({ search: "PreTax" }));
    dispatch(fetchCountries());
  }, [dispatch]);

  useEffect(() => {
    // Hide footer when this component is mounted
    const footer = document.querySelector("footer");
    if (footer) {
      footer.style.display = "none";
    }

    // Restore footer when leaving this page
    return () => {
      if (footer) {
        footer.style.display = "";
      }
    };
  }, []);

  const handleFormSubmit = (data) => {
    setIsFormSubmitting(true);
    const formData = new FormData();

    setCompanyName(data["Company Name"]);
    formData.append("clientId", clientId);
    formData.append("name", data["Company Name"]);
    formData.append("email", data["Email Address"]);
    formData.append("phone", data["Phone Number"]);
    // formData.append("dateOfBirth", data["Date of Birth"]);
    formData.append("cityId", data["CityId"] || data["City"]);
    formData.append("postalCode", data["ZIP Code"]);
    formData.append("addressLine1", data["Address"]);
    formData.append("addressLine2", data["Address"]);
    formData.append("password", data["Password"]);

    if (profileImage) {
      formData.append("file", profileImage);
    }

    if (data["Contract File"]) {
      formData.append("contractFile", data["Contract File"]);
    }

    formData.append("payrollCycle", data["Payroll Cycle"] || "");
    formData.append("taxScheduleType", data["Tax Reminder"] || "");

    const taxIds = formulaList
      .filter((formula) => data[formula.id])
      .map((formula) => formula.id);

    taxIds.forEach((id) => {
      formData.append("taxIds", id);
    });

    dispatch(createCompany(formData));
  };

  const handleSuccessPopupClose = () => {
    setShowSuccessPopup(false);
    dispatch(resetStatus());
    navigate("/companies");
  };

  const steps = [
    {
      stepId: "Step1",
      title: "Company Information",
      subtitle: "",
      showImage: true,
      imageSrc: preview || clint_image_icon,
      fields: [
        {
          label: "Company Name",
          placeholder: "Enter Company Name",
          required: true,
        },
        {
          label: "Email Address",
          type: "email",
          placeholder: "Enter Email Address",
          required: true,
        },
        {
          label: "Phone Number",
          type: "tel",
          placeholder: "Enter Phone Number",
          required: true,
          maxLength: 10,
        },

        {
          label: "Password",
          type: "password",
          placeholder: "Enter Password",
          required: true,
        },
      ],
      imageRequired: false,
      headings: [""],
      showCheckbox: false,
    },
    {
      stepId: "Step2",
      title: "Company Address",
      subtitle: "",
      showImage: false,
      fields: [
        {
          label: "Country",
          type: "dropdown",
          placeholder: "Select Country",
          required: true,
          options: [
            ...(countries?.map((c) => ({
              value: String(c.id),
              label: c.name,
            })) || []),
            { value: "__add_new__", label: "Add new country" },
          ],
        },
        {
          label: "State",
          type: "dropdown",
          placeholder: "Select State",
          required: true,
          options: [
            ...(states?.map((s) => ({
              value: String(s.id),
              label: s.name,
            })) || []),
            { value: "__add_new__", label: "Add new state" },
          ],
        },
        {
          label: "City",
          type: "dropdown",
          placeholder: "Select City",
          required: true,
          options: [
            ...(cities?.map((c) => ({
              value: String(c.id),
              label: c.name,
            })) || []),
            { value: "__add_new__", label: "Add new city" },
          ],
        },
        {
          label: "ZIP Code",
          type: "text",
          placeholder: "Enter ZIP Code",
          required: true,
        },
        {
          label: "Address",
          type: "text",
          placeholder: "Enter Address",
          required: true,
        },
      ],
      headings: [" "],
      showCheckbox: false,
    },
    {
      stepId: "Step3",
      title: "Company Pay & Tax Setup",
      subtitle: "Tax Settings",
      showImage: false,
      fields: [
        {
          label: "Payroll Cycle",
          type: "select",
          placeholder: "Select payroll cycle",
          options: [
            { value: "Monthly", label: "Monthly" },
            { value: "Weekly", label: "Weekly" },
            { value: "BiWeekly", label: "BiWeekly" },
            { value: "SemiMonthly", label: "SemiMonthly" },
          ],
          required: true,
        },
        {
          label: "Contract File",
          type: "file",
          placeholder: "Upload PDF",
          accept: ".pdf",
          required: false,
        },
        {
          label: "Tax Reminder",
          type: "select",
          placeholder: "Select Reminder Type",
          options: [
            { value: 1, label: "Immediate" },
            { value: 2, label: "Monthly" },
          ],
          required: true,
        },
      ],
      showCheckbox: true,
      checkboxes:
        formulaList?.map((item) => ({
          id: item.id,
          label: item.code,
          spanText: `(${item.title || item.name})`,
        })) || [],
    },
  ];
  return (
    <>
      <div className="add_new_clint">
        <div className="container-fluid h-100">
          <div className="row h-100">
            <div className="left-content col-md-6 bg_light_grey h-100">
              <div className="content pt_80">
                <h4 className="mb_30">Start Smart. Add Your Next Company.</h4>
                <p>
                  Quickly create and configure a new company profile, assign
                  companies, and set payroll preferences — all in a few clicks.
                </p>
              </div>
            </div>

            <div className="col-md-6 bg_white h-100 px-0">
              {/* Hidden file input for profile image */}
              <input
                type="file"
                accept="image/*"
                id="profileImageInput"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setProfileImage(file);
                    setPreview(URL.createObjectURL(file));
                  }
                }}
              />

              {/* Image click handled inside Steps component */}
              <Steps
                steps={steps}
                initialValues={{
                  ...locationFormValues,
                }}
                onSubmit={handleFormSubmit}
                onImageClick={(stepId) => {
                  if (stepId === "Step1") {
                    document.getElementById("profileImageInput").click();
                  }
                }}
                imageSelected={!!profileImage}
                loading={loading}
                onOpenLocationPopup={handleOpenLocationPopup}
              />
            </div>
          </div>
        </div>
      </div>
      <StatusPopup
        show={!!errorMessage}
        type="error"
        title="Error"
        message={errorMessage}
        handleClose={() => dispatch(resetStatus())}
      />
      <EditSuccessPopup
        show={showSuccessPopup}
        onHide={handleSuccessPopupClose}
        entityType="company"
        operationType="add"
        title="Company Created Successfully"
      />

      <CountryCreatePopup
        show={showCountryPopup}
        onHide={() => setShowCountryPopup(false)}
        onSuccess={handleCountryCreated}
      />

      <StateCreatePopup
        show={showStatePopup}
        onHide={() => setShowStatePopup(false)}
        onSuccess={handleStateCreated}
        selectedCountryId={selectedCountryId}
      />

      <CityCreatePopup
        show={showCityPopup}
        onHide={() => setShowCityPopup(false)}
        onSuccess={handleCityCreated}
        selectedStateId={selectedStateId}
      />
    </>
  );
};

export default AddNewCompany;
